import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const PaymentStatusBanner = ({ 
  status = null, // { type: 'processing' | 'success' | 'error' | 'warning', message: string, details?: string, action?: { label: string, onClick: function } }
  onDismiss,
  autoHide = true,
  autoHideDelay = 5000,
  position = "top", // "top" | "bottom"
  className = ""
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (status) {
      setIsVisible(true);
      setProgress(100);

      if (autoHide && status.type !== 'processing') {
        const progressInterval = setInterval(() => {
          setProgress(prev => {
            if (prev <= 0) {
              clearInterval(progressInterval);
              handleDismiss();
              return 0;
            }
            return prev - (100 / (autoHideDelay / 100));
          });
        }, 100);

        return () => clearInterval(progressInterval);
      }
    } else {
      setIsVisible(false);
    }
  }, [status, autoHide, autoHideDelay]);

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  const getStatusConfig = (type) => {
    const configs = {
      processing: {
        bgColor: 'bg-secondary',
        textColor: 'text-secondary-foreground',
        icon: 'Loader2',
        iconClass: 'animate-spin'
      },
      success: {
        bgColor: 'bg-success',
        textColor: 'text-success-foreground',
        icon: 'CheckCircle',
        iconClass: ''
      },
      error: {
        bgColor: 'bg-error',
        textColor: 'text-error-foreground',
        icon: 'XCircle',
        iconClass: ''
      },
      warning: {
        bgColor: 'bg-warning',
        textColor: 'text-warning-foreground',
        icon: 'AlertTriangle',
        iconClass: ''
      }
    };
    return configs[type] || configs.processing;
  };

  if (!status || !isVisible) return null;

  const config = getStatusConfig(status.type);
  const positionClasses = position === "bottom" ? "bottom-0" : "top-0";

  return (
    <div 
      className={`fixed left-0 right-0 z-1005 ${positionClasses} transform transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : position === "bottom" ? 'translate-y-full' : '-translate-y-full'
      } ${className}`}
    >
      <div className={`${config.bgColor} ${config.textColor} shadow-medium`}>
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon 
                name={config.icon} 
                size={20} 
                className={config.iconClass}
              />
              <div className="flex-1">
                <div className="font-medium text-sm">
                  {status.message}
                </div>
                {status.details && (
                  <div className="text-xs opacity-90 mt-1">
                    {status.details}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {status.action && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={status.action.onClick}
                  className={`${config.textColor} hover:bg-white/10`}
                >
                  {status.action.label}
                </Button>
              )}
              
              {status.type !== 'processing' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDismiss}
                  className={`${config.textColor} hover:bg-white/10 w-8 h-8`}
                >
                  <Icon name="X" size={16} />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Progress bar for auto-hide */}
        {autoHide && status.type !== 'processing' && (
          <div className="h-1 bg-black/10">
            <div 
              className="h-full bg-white/30 transition-all duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Hook for managing payment status
export const usePaymentStatus = () => {
  const [status, setStatus] = useState(null);

  const showStatus = (statusData) => {
    setStatus(statusData);
    
    // Dispatch custom event for header component
    window.dispatchEvent(new CustomEvent('paymentStatusUpdate', {
      detail: statusData
    }));
  };

  const hideStatus = () => {
    setStatus(null);
  };

  const showProcessing = (message = "Processing payment...", details = null) => {
    showStatus({
      type: 'processing',
      message,
      details
    });
  };

  const showSuccess = (message = "Payment successful!", details = null, action = null) => {
    showStatus({
      type: 'success',
      message,
      details,
      action
    });
  };

  const showError = (message = "Payment failed", details = null, action = null) => {
    showStatus({
      type: 'error',
      message,
      details,
      action
    });
  };

  const showWarning = (message = "Payment requires attention", details = null, action = null) => {
    showStatus({
      type: 'warning',
      message,
      details,
      action
    });
  };

  return {
    status,
    showStatus,
    hideStatus,
    showProcessing,
    showSuccess,
    showError,
    showWarning
  };
};

export default PaymentStatusBanner;